import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, Ban, Calculator, CheckCircle2, Coins, FileText, Loader2,
    MessageSquareWarning, Package, RefreshCw, ShoppingBag, TrendingUp,
    Users, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


function KpiCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
    const toneCls = {
        primary: "text-[var(--color-primary)]",
        secondary: "text-[var(--color-secondary)]",
        success: "text-[var(--color-success)]",
        warning: "text-neutral-200",
        alert: "text-[var(--color-alert)]",
    }[tone];
    return (
        <div data-testid={testid} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
            {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
        </div>
    );
}


function StatusBadge({ status }) {
    const cls = {
        pending_review: "text-neutral-200 bg-neutral-800 border-neutral-700",
        changes_requested: "text-neutral-200 bg-neutral-800 border-neutral-700",
        published: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/40",
        rejected: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
        pending_admin_review: "text-neutral-200 bg-neutral-800 border-neutral-700",
        approved_pending_transfer: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/40",
    }[status] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {(status || "").replace(/_/g, " ")}
        </span>
    );
}


function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2 ${
                active ? "border-[var(--color-primary)] text-text-main bg-[var(--color-primary)]/10"
                       : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
            }`}
        >
            <Icon className="h-3 w-3" /> {label}
            {typeof count === "number" && <span className="text-[10px] tabular-nums opacity-70">({count})</span>}
        </button>
    );
}


function ActionModal({ open, title, subtitle, tone, confirmLabel, onConfirm, onCancel, busy, children }) {
    const toneCls = {
        primary: "bg-white text-black",
        alert: "bg-[var(--color-alert)] text-white",
        warning: "bg-[#A3A3A3] text-black",
        success: "bg-white text-black",
    }[tone] || "bg-white text-black";
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onCancel} />
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div data-testid="mp3-admin-confirm-modal" className="pointer-events-auto w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base font-semibold">{title}</h3>
                                    {subtitle && <p className="text-xs text-text-secondary mt-1">{subtitle}</p>}
                                </div>
                                <button onClick={onCancel} disabled={busy} className="text-text-secondary hover:text-text-main disabled:opacity-40"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1">{children}</div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button onClick={onCancel} disabled={busy} className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40">Cancel</button>
                                <button onClick={onConfirm} disabled={busy} data-testid="mp3-admin-confirm-ok" className={`text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50 ${toneCls}`}>
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                    {confirmLabel}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


function fmtEur(v) { return `$${Number(v || 0).toFixed(2)}`; }
function fmtCredits(v) { return `${Number(v || 0).toLocaleString()} cr`; }
function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }


export default function AdminMarketplaceV3() {
    const { user: me } = useAuth();
    const [tab, setTab] = useState("moderation");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [busy, setBusy] = useState(false);

    const [overview, setOverview] = useState({});
    const [moderation, setModeration] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [tags, setTags] = useState([]);

    const [confirmState, setConfirmState] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [requestedChanges, setRequestedChanges] = useState("");

    const load = useCallback(async () => {
        try {
            const [ov, mod, po, tg] = await Promise.all([
                api.mp3AdminOverview(),
                api.mp3AdminModeration(null, 100),
                api.mp3AdminPayouts({ limit: 100 }),
                api.mp3AdminPopularTags(20),
            ]);
            setOverview(ov || {});
            setModeration(mod?.items || []);
            setPayouts(po?.items || []);
            setTags(tg?.items || []);
            setDisabled(false);
        } catch (e) {
            if (e.status === 503) setDisabled(true);
            else if (e.status !== 403) toast.error(e.message || "Failed to load");
        }
    }, []);

    useEffect(() => {
        (async () => { setLoading(true); await load(); setLoading(false); })();
    }, [load]);

    const refresh = async () => {
        setRefreshing(true); await load(); setRefreshing(false);
    };

    // ---- actions ----
    const approve = async () => {
        const asset = confirmState?.asset;
        if (!asset) return;
        setBusy(true);
        try {
            const r = await api.mp3AdminApprove(asset.id);
            toast.success(`Approved · ${r.seo_suggestions_created} SEO suggestion(s) + creator notified`);
            await load();
            setConfirmState(null);
        } catch (e) { toast.error(e.message || "Approve failed"); }
        finally { setBusy(false); }
    };
    const reject = async () => {
        const asset = confirmState?.asset;
        if (!asset) return;
        if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
        setBusy(true);
        try {
            await api.mp3AdminReject(asset.id, rejectReason.trim());
            toast.success("Rejected · creator notified");
            await load();
            setConfirmState(null); setRejectReason("");
        } catch (e) { toast.error(e.message || "Reject failed"); }
        finally { setBusy(false); }
    };
    const requestChanges = async () => {
        const asset = confirmState?.asset;
        if (!asset) return;
        const list = requestedChanges.split("\n").map((s) => s.trim()).filter(Boolean);
        if (!list.length) { toast.error("Add at least one requested change"); return; }
        setBusy(true);
        try {
            await api.mp3AdminRequestChanges(asset.id, list);
            toast.success("Changes requested · creator notified");
            await load();
            setConfirmState(null); setRequestedChanges("");
        } catch (e) { toast.error(e.message || "Request changes failed"); }
        finally { setBusy(false); }
    };
    const approvePayout = async () => {
        const po = confirmState?.payout;
        if (!po) return;
        setBusy(true);
        try {
            const r = await api.mp3AdminApprovePayout(po.id);
            toast.success(`Payout approved · mock ledger · stripe_call_made=${r.stripe_call_made}`);
            await load();
            setConfirmState(null);
        } catch (e) { toast.error(e.message || "Approve failed"); }
        finally { setBusy(false); }
    };
    const forceMonthly = async () => {
        setBusy(true);
        try {
            const r = await api.mp3AdminForceMonthlyCalc();
            toast.success(`Monthly calc: ${r.created_count} payout(s) · ${r.skipped_below_min?.length || 0} skipped`);
            await load();
        } catch (e) { toast.error(e.message || "Calc failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <main className="flex-1 flex flex-col min-w-0">
                <Header />
                <div className="flex-1 p-6 md:p-8 space-y-6 max-w-[1200px] w-full mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                                <ShoppingBag className="h-3 w-3 text-[var(--color-secondary)]" />
                                Marketplace v3 · Admin · Phase 16c
                            </div>
                            <h1 data-testid="mp3-admin-title" className="text-2xl md:text-3xl font-semibold tracking-tight">
                                Marketplace moderation
                            </h1>
                            <p className="text-xs text-text-secondary mt-1 max-w-xl">
                                Wrapper over Phase 5 marketplace. Manual moderation · 70/30 split · Mock payout ledger (NO Stripe Connect until Fase 17).
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button data-testid="mp3-admin-refresh" onClick={refresh} disabled={refreshing || loading} className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 text-text-secondary hover:text-text-main disabled:opacity-40 flex items-center gap-2">
                                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Refresh
                            </button>
                            <button data-testid="mp3-admin-force-monthly" onClick={forceMonthly} disabled={busy || loading || disabled} className="text-xs rounded-full px-4 py-1.5 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40">
                                <Calculator className="h-3 w-3" /> Force monthly calc
                            </button>
                        </div>
                    </div>

                    {disabled ? (
                        <div className="rounded-2xl border border-neutral-700 bg-neutral-900 p-6 flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-neutral-200 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-semibold text-neutral-200">Marketplace v3 is disabled</h3>
                                <p className="text-xs text-text-secondary mt-1">Flip <code>MARKETPLACE_V3_ENABLED=true</code> in system settings to re-enable.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <KpiCard icon={FileText} label="Pending review" value={overview.pending_review_count ?? 0} tone="warning" testid="mp3-admin-kpi-pending" />
                                <KpiCard icon={Package} label="Published" value={overview.published_count ?? 0} tone="success" testid="mp3-admin-kpi-published" />
                                <KpiCard icon={Coins} label="Revenue 30d" value={fmtCredits(overview.revenue_last_30d_credits)} hint={`≈ ${fmtEur((overview.revenue_last_30d_credits || 0) / 100)}`} tone="secondary" testid="mp3-admin-kpi-rev" />
                                <KpiCard icon={Users} label="Active creators" value={overview.active_creators_count ?? 0} tone="primary" testid="mp3-admin-kpi-creators" />
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <TabBtn active={tab === "moderation"} onClick={() => setTab("moderation")} icon={MessageSquareWarning} label="Moderation queue" count={moderation.length} testid="mp3-admin-tab-mod" />
                                <TabBtn active={tab === "payouts"} onClick={() => setTab("payouts")} icon={Wallet} label="Payouts" count={payouts.length} testid="mp3-admin-tab-payouts" />
                                <TabBtn active={tab === "tags"} onClick={() => setTab("tags")} icon={TrendingUp} label="Top tags" count={tags.length} testid="mp3-admin-tab-tags" />
                            </div>

                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {loading ? (
                                    <div className="p-10 flex items-center justify-center text-text-secondary text-xs gap-2">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                    </div>
                                ) : tab === "moderation" ? (
                                    <ModerationTable rows={moderation} onAction={(kind, asset) => setConfirmState({ kind, asset })} />
                                ) : tab === "payouts" ? (
                                    <PayoutsTable rows={payouts} onApprove={(payout) => setConfirmState({ kind: "approve_payout", payout })} />
                                ) : (
                                    <TagsTable rows={tags} />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Confirm modals */}
            <ActionModal
                open={confirmState?.kind === "approve"}
                title={`Approve '${confirmState?.asset?.title || ""}'?`}
                subtitle="This will publish the asset, create SEO suggestions, and email the creator."
                tone="success" confirmLabel="Approve"
                onConfirm={approve} onCancel={() => setConfirmState(null)} busy={busy}
            />

            <ActionModal
                open={confirmState?.kind === "reject"}
                title={`Reject '${confirmState?.asset?.title || ""}'?`}
                subtitle="Creator will be notified with your reason."
                tone="alert" confirmLabel="Reject"
                onConfirm={reject} onCancel={() => { setConfirmState(null); setRejectReason(""); }} busy={busy}
            >
                <label className="block text-xs text-text-secondary">
                    Reason
                    <textarea
                        data-testid="mp3-admin-reject-reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={3}
                        placeholder="Why is this asset being rejected?"
                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main"
                    />
                </label>
            </ActionModal>

            <ActionModal
                open={confirmState?.kind === "request_changes"}
                title={`Request changes on '${confirmState?.asset?.title || ""}'?`}
                subtitle="One requested change per line (max 10)"
                tone="warning" confirmLabel="Send feedback"
                onConfirm={requestChanges} onCancel={() => { setConfirmState(null); setRequestedChanges(""); }} busy={busy}
            >
                <label className="block text-xs text-text-secondary">
                    Requested changes
                    <textarea
                        data-testid="mp3-admin-changes-list"
                        value={requestedChanges}
                        onChange={(e) => setRequestedChanges(e.target.value)}
                        rows={5}
                        placeholder="- Fix broken links in step 2&#10;- Update pricing table&#10;- Add missing screenshots"
                        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main font-mono"
                    />
                </label>
            </ActionModal>

            <ActionModal
                open={confirmState?.kind === "approve_payout"}
                title={`Approve payout $${confirmState?.payout?.equivalent_eur?.toFixed?.(2)}?`}
                subtitle="Mock ledger only · NO Stripe Connect call · creator will be notified."
                tone="primary" confirmLabel="Approve payout"
                onConfirm={approvePayout} onCancel={() => setConfirmState(null)} busy={busy}
            />
        </div>
    );
}


function ModerationTable({ rows, onAction }) {
    if (!rows.length) {
        return <div className="p-10 text-center text-xs text-text-secondary">No assets in moderation queue. 🎉</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="mp3-admin-mod-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">Title</th>
                        <th className="text-left px-4 py-3">Type</th>
                        <th className="text-right px-4 py-3">Price</th>
                        <th className="text-right px-4 py-3">Version</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3">Updated</th>
                        <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.map((r) => (
                        <tr key={r.id} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 text-text-main max-w-[240px] truncate">{r.title}</td>
                            <td className="px-4 py-3 text-text-secondary">{r.asset_type}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-secondary)]">{fmtCredits(r.price_credits)}</td>
                            <td className="px-4 py-3 text-right font-mono text-text-secondary">v{r.current_version}</td>
                            <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                            <td className="px-4 py-3 text-text-secondary">{fmtDate(r.updated_at)}</td>
                            <td className="px-4 py-3 text-right">
                                <div className="inline-flex items-center gap-1">
                                    <button
                                        onClick={() => onAction("approve", r)}
                                        data-testid={`mp3-admin-approve-${r.slug}`}
                                        className="text-[10px] rounded-full px-2 py-1 bg-white/10 border border-white/20 text-white hover:bg-white/15"
                                    >
                                        approve
                                    </button>
                                    <button
                                        onClick={() => onAction("request_changes", r)}
                                        data-testid={`mp3-admin-changes-${r.slug}`}
                                        className="text-[10px] rounded-full px-2 py-1 bg-[#A3A3A3]/10 border border-[#A3A3A3]/40 text-[#A3A3A3] hover:bg-[#A3A3A3]/20"
                                    >
                                        changes
                                    </button>
                                    <button
                                        onClick={() => onAction("reject", r)}
                                        data-testid={`mp3-admin-reject-${r.slug}`}
                                        className="text-[10px] rounded-full px-2 py-1 bg-[var(--color-alert)]/10 border border-[var(--color-alert)]/40 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/20"
                                    >
                                        reject
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function PayoutsTable({ rows, onApprove }) {
    if (!rows.length) {
        return <div className="p-10 text-center text-xs text-text-secondary">No payouts calculated yet. Click <span className="text-text-main">Force monthly calc</span>.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="mp3-admin-payouts-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">Creator</th>
                        <th className="text-left px-4 py-3">Month</th>
                        <th className="text-right px-4 py-3">Credits</th>
                        <th className="text-right px-4 py-3">USD</th>
                        <th className="text-right px-4 py-3">Purchases</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.map((r) => (
                        <tr key={r.id} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 font-mono text-text-secondary text-[10px]">{(r.creator_id || "").slice(0, 12)}…</td>
                            <td className="px-4 py-3 text-text-secondary">{r.payout_month}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-secondary)]">{fmtCredits(r.total_credits)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-success)]">{fmtEur(r.equivalent_eur)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-text-secondary">{(r.purchase_ids || []).length}</td>
                            <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                            <td className="px-4 py-3 text-right">
                                {r.status === "pending_admin_review" ? (
                                    <button
                                        onClick={() => onApprove(r)}
                                        data-testid={`mp3-admin-payout-approve-${r.id}`}
                                        className="text-[10px] rounded-full px-2 py-1 bg-white/10 border border-white/20 text-white hover:bg-white/15"
                                    >
                                        approve
                                    </button>
                                ) : (
                                    <span className="text-[10px] text-text-secondary">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function TagsTable({ rows }) {
    if (!rows.length) {
        return <div className="p-10 text-center text-xs text-text-secondary">No tags on published assets yet.</div>;
    }
    return (
        <div className="p-4 flex flex-wrap gap-2">
            {rows.map((r) => (
                <span key={r.tag} data-testid={`mp3-admin-tag-${r.tag}`} className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)]">
                    <span className="text-text-main">{r.tag}</span>
                    <span className="text-text-secondary tabular-nums">{r.count}</span>
                </span>
            ))}
        </div>
    );
}
